import type { Slide } from "pages/media/types";
import { SlideLayout } from "../types";

const KEY = "components.templates.TemplatesBoard.storyboardSlidesThumbnails";

export type AddableSlideLayout = Exclude<SlideLayout, SlideLayout.TITLE>;

type Translate = (key: string) => string;

/**
 * Build a Slide payload with sensible localized defaults for the chosen
 * layout. Title slides (transitions) are not addable from the editor — the
 * chapter owns them.
 */
export const buildDefaultSlide = (
  uuid: string,
  id: string,
  sectionId: string,
  layout: AddableSlideLayout,
  t: Translate
): Slide => {
  // generated: false — this slide was added by the user, not produced by the
  // generation pipeline. The backend treats user-added slides differently
  // (e.g. they aren't overwritten by re-runs of slide generation).
  const base = { uuid, id, section_id: sectionId, generated: false };

  // image_element is required on every placeholders variant since the latest
  // schema. Default to an empty image element; for layouts that explicitly
  // surface a main image (IMAGE, PARAGRAPH_IMAGE) we still seed a description.
  const defaultImageElement = {
    description: t(`${KEY}.defaultSubtitle`),
    media_type: "image" as const,
  };

  switch (layout) {
    case SlideLayout.BULLET_POINT:
      return {
        ...base,
        slide_type: SlideLayout.BULLET_POINT,
        placeholders: {
          title: t(`${KEY}.defaultTitle`),
          bullet_points: [
            t(`${KEY}.defaultBulletPoint`),
            t(`${KEY}.defaultBulletPoint`),
            t(`${KEY}.defaultBulletPoint`),
          ],
          image_element: defaultImageElement,
        },
      };
    case SlideLayout.QUOTE:
      return {
        ...base,
        slide_type: SlideLayout.QUOTE,
        placeholders: {
          quote: t(`${KEY}.defaultQuote`),
          image_element: defaultImageElement,
        },
      };
    case SlideLayout.FIGURE:
      return {
        ...base,
        slide_type: SlideLayout.FIGURE,
        placeholders: {
          title: t(`${KEY}.defaultTitle`),
          figure: {
            figure: t(`${KEY}.defaultFigureNumber`),
            unit: t(`${KEY}.defaultFigureUnits`),
            short_description: t(`${KEY}.defaultSubtitle`),
          },
          image_element: defaultImageElement,
        },
      };
    case SlideLayout.IMAGE:
      return {
        ...base,
        slide_type: SlideLayout.IMAGE,
        placeholders: {
          title: t(`${KEY}.defaultTitle`),
          image_element: defaultImageElement,
        },
      };
    case SlideLayout.DOUBLE_PARAGRAPH:
      return {
        ...base,
        slide_type: SlideLayout.DOUBLE_PARAGRAPH,
        placeholders: {
          title: t(`${KEY}.defaultTitle`),
          paragraph_1: t(`${KEY}.defaultBody`),
          paragraph_2: t(`${KEY}.defaultBody`),
          image_element: defaultImageElement,
        },
      };
    case SlideLayout.PARAGRAPH_IMAGE:
      return {
        ...base,
        slide_type: SlideLayout.PARAGRAPH_IMAGE,
        placeholders: {
          title: t(`${KEY}.defaultTitle`),
          text: t(`${KEY}.defaultBody`),
          image_element: defaultImageElement,
        },
      };
  }
};
